import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import {
  getDialogReview,
  remarkCategoryLabel,
  remarkStatusLabel,
  sendReviewChat,
  severityClassName,
  type ProposedRemark,
  type Remark,
  type ReviewMessage,
} from '@/entities/remarks';
import { ApiError } from '@/shared/api';
import { cn } from '@/shared/lib/utils';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import { Textarea } from '@/shared/ui/shadcn/textarea';

export interface PlaygroundReviewChatProps {
  dialogId: string | null;
  /** Bumps refetch after new agent replies (so remark links stay current). */
  revision?: string | null;
  onSelectRemark?: (remark: Remark) => void;
  className?: string;
}

function ProposedList({
  items,
  busy,
  onConfirm,
  onReject,
}: {
  items: ProposedRemark[];
  busy: boolean;
  onConfirm: () => void;
  onReject: () => void;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
      <div className="text-sm font-medium">Proposed remarks ({items.length})</div>
      <ul className="space-y-1.5">
        {items.map((item, index) => (
          <li
            key={`${item.title}-${index}`}
            className="rounded border bg-background/80 p-2 text-xs"
          >
            <div className="mb-1 flex flex-wrap items-center gap-1.5">
              <Badge
                variant="outline"
                className={cn('font-normal', severityClassName(item.severity))}
              >
                {item.severity}
              </Badge>
              <Badge variant="outline" className="font-normal">
                {remarkCategoryLabel(item.category)}
              </Badge>
            </div>
            <div className="font-medium text-foreground">{item.title}</div>
            <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{item.body}</p>
          </li>
        ))}
      </ul>
      <div className="flex gap-2 pt-1">
        <Button type="button" size="sm" onClick={onConfirm} disabled={busy}>
          Create {items.length > 1 ? `${items.length} remarks` : 'remark'}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onReject} disabled={busy}>
          Discard
        </Button>
      </div>
    </div>
  );
}

function RemarkChip({ remark, onSelect }: { remark: Remark; onSelect?: (remark: Remark) => void }) {
  return (
    <button
      type="button"
      onClick={() => onSelect?.(remark)}
      className="flex w-full items-start gap-2 rounded-md border border-border/60 bg-background px-2 py-1.5 text-left text-xs hover:bg-muted/60"
    >
      <Badge
        variant="outline"
        className={cn('shrink-0 font-normal', severityClassName(remark.severity))}
      >
        {remark.severity}
      </Badge>
      <span className="min-w-0 flex-1 truncate">{remark.title}</span>
      <Badge variant="secondary" className="shrink-0 font-normal">
        {remarkStatusLabel(remark.status)}
      </Badge>
    </button>
  );
}

export function PlaygroundReviewChat({
  dialogId,
  revision,
  onSelectRemark,
  className,
}: PlaygroundReviewChatProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<ProposedRemark[]>([]);
  const [optimistic, setOptimistic] = useState<ReviewMessage[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const reviewQuery = useQuery({
    queryKey: ['dialog-review', dialogId, revision],
    queryFn: () => getDialogReview(dialogId!),
    enabled: Boolean(dialogId),
  });

  useEffect(() => {
    setPending([]);
    setOptimistic([]);
    setDraft('');
  }, [dialogId]);

  const messages = [...(reviewQuery.data?.messages ?? []), ...optimistic];
  const remarks = reviewQuery.data?.remarks ?? [];

  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages.length, pending.length]);

  const chatMutation = useMutation({
    mutationFn: (input: {
      content: string;
      allow_mutations?: boolean;
      confirm_remarks?: ProposedRemark[];
    }) => sendReviewChat(dialogId!, input),
    onSuccess: async (result) => {
      setOptimistic([]);
      setPending(result.pending_remarks ?? []);
      if (result.created_remarks?.length) {
        toast.success(`Created ${result.created_remarks.length} remark(s)`);
        await queryClient.invalidateQueries({ queryKey: ['remarks'] });
        await queryClient.invalidateQueries({ queryKey: ['playground-sessions'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['dialog-review', dialogId] });
    },
    onError: (error) => {
      setOptimistic([]);
      toast.error(error instanceof ApiError ? error.message : 'Review chat failed');
    },
  });

  const busy = chatMutation.isPending || !dialogId;

  const send = () => {
    const content = draft.trim();
    if (!content || busy) return;
    setOptimistic([
      {
        id: `optimistic-${Date.now()}`,
        role: 'user',
        content,
        created_at: new Date().toISOString(),
      },
    ]);
    setDraft('');
    setPending([]);
    chatMutation.mutate({ content });
  };

  const confirm = () => {
    if (busy || pending.length === 0) return;
    const content = 'Подтверждаю, создай замечания.';
    setOptimistic([
      {
        id: `optimistic-${Date.now()}`,
        role: 'user',
        content,
        created_at: new Date().toISOString(),
      },
    ]);
    chatMutation.mutate({ content, allow_mutations: true, confirm_remarks: pending });
  };

  if (!dialogId) {
    return (
      <p className={cn('px-3 py-6 text-center text-xs text-muted-foreground', className)}>
        Start a chat to leave remarks about this dialog.
      </p>
    );
  }

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      {remarks.length > 0 ? (
        <div className="shrink-0 space-y-1.5 border-b border-border p-3">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
              Remarks ({remarks.length})
            </p>
            <Link
              to={`/remarks?dialog_id=${encodeURIComponent(dialogId)}`}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              Board <ExternalLink className="size-3" />
            </Link>
          </div>
          <div className="max-h-32 space-y-1 overflow-y-auto">
            {remarks.map((remark) => (
              <RemarkChip key={remark.id} remark={remark} onSelect={onSelectRemark} />
            ))}
          </div>
        </div>
      ) : null}

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {reviewQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading review thread…
          </div>
        ) : null}
        {!reviewQuery.isLoading && messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Describe what went wrong in this dialog. The reviewer grounds your feedback in the
            transcript and run diagnostics, proposes remarks, and files them after you confirm.
          </p>
        ) : null}
        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              'rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
              message.role === 'user'
                ? 'ml-6 bg-primary text-primary-foreground'
                : 'mr-6 bg-muted text-foreground',
            )}
          >
            {message.content}
          </div>
        ))}
        {chatMutation.isPending ? (
          <div className="mr-6 flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Reviewing…
          </div>
        ) : null}
        {pending.length > 0 ? (
          <ProposedList
            items={pending}
            busy={busy}
            onConfirm={confirm}
            onReject={() => setPending([])}
          />
        ) : null}
      </div>

      <div className="border-t p-3">
        <div className="flex gap-2">
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="What should ARIA have done differently?"
            className="min-h-[72px] resize-none"
            disabled={busy}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />
          <Button type="button" size="icon" onClick={send} disabled={busy || !draft.trim()}>
            {chatMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Send className="size-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
